import { useAuthInfo } from '@propelauth/react';
import { useNavigate } from 'react-router-dom';
import { ReactNode, useEffect, useMemo } from 'react';
import axios, { AxiosError, AxiosHeaders } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { notifications } from '@mantine/notifications';
import ApiClientContext from '@/context/ApiClientContext/ApiClientContext.tsx';
import { identifyClient } from './identity';
import { getHttpErrorMessage, isRetryableReadHttpStatus } from '@/services/httpRetry.ts';

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };
type RefreshAccessToken = () => Promise<string | undefined>;

let refreshAccessTokenPromise: Promise<string | undefined> | null = null;

interface ApiClientProviderProps {
  children: ReactNode;
}

export const ApiClientProvider = ({ children }: ApiClientProviderProps) => {
  const { accessToken, refreshAuthInfo, tokens, user } = useAuthInfo();

  const navigate = useNavigate();

  const apiClient = useMemo(() => {
    const client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    });
    if (user?.userId) identifyClient(client, user.userId);
    return client;
  }, [accessToken, user?.userId]);

  useEffect(() => {
    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        // 1. Detect network / offline errors and *skip* toasts
        if (axios.isAxiosError(error) && !error.response) {
          // e.g. user turned off Wi-Fi, DNS failure, CORS preflight failure, etc.
          // You can optionally handle a global "offline" UI elsewhere.
          return Promise.reject(error);
        }

        const axiosError = error as AxiosError;
        const status = axiosError.response?.status;
        const data = axiosError.response?.data;

        if (isTokenExpiredResponse(status, data)) {
          const originalRequest = axiosError.config as RetriableRequestConfig | undefined;

          if (originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshedAccessToken = await getRefreshedAccessToken(async () => {
              await refreshAuthInfo();
              return tokens.getAccessToken();
            });

            if (refreshedAccessToken) {
              originalRequest.headers = AxiosHeaders.from(originalRequest.headers);
              originalRequest.headers.set('Authorization', `Bearer ${refreshedAccessToken}`);
              return apiClient.request(originalRequest);
            }
          }
        }

        // 2. Your existing logic now only runs for real HTTP responses
        const isRetryableGetError =
          axiosError.config?.method?.toLowerCase() === 'get' &&
          status !== undefined &&
          isRetryableReadHttpStatus(status);

        if (!axiosError.config?.suppressGlobalErrorToast && status !== 404 && !isRetryableGetError) {
          notifications.show({
            title: 'We ran into an error making your request',
            message: 'Error: ' + getHttpErrorMessage(axiosError),
            color: 'red',
          });
        }

        if (status === 403 && !axiosError.config?.handleForbiddenLocally) {
          notifications.show({
            title: 'You do not have permission to perform this action',
            message: String(data) || 'Forbidden',
            color: 'red',
          });
          navigate('/automations');
        }

        return Promise.reject(error);
      },
    );

    return () => {
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, [apiClient, navigate, refreshAuthInfo, tokens]);

  return <ApiClientContext.Provider value={apiClient}>{children}</ApiClientContext.Provider>;
};

function isTokenExpiredResponse(status: number | undefined, data: unknown) {
  if (status !== 401) return false;

  if (data && typeof data === 'object' && 'code' in data) {
    return data.code === 'token_expired';
  }

  return false;
}

function getRefreshedAccessToken(refreshAccessToken: RefreshAccessToken) {
  if (!refreshAccessTokenPromise) {
    refreshAccessTokenPromise = refreshAccessToken().finally(() => {
      refreshAccessTokenPromise = null;
    });
  }

  return refreshAccessTokenPromise;
}
